/**
 * Walk-In Cohort Analytics
 *
 * Payload from GET /admin/bridge-club/walk-ins/analytics/ — unconverted
 * UnmatchedVisitors only. The headline metrics are:
 *   - invite-priority count (3+ visits, $200+ spend, or 500+ shadow points)
 *   - total shadow points across the cohort + lost_credit_dollars: how
 *     much $10 in store credit we already "owe" people we haven't claimed.
 *   - top products inferred from the invoice→POSTransaction join
 *
 * Each invite-priority row is clickable into UnmatchedVisitorDetail.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface PriorityVisitor {
  visitor_id: number;
  cardholder_name: string;
  visit_count: number;
  total_spend: number;
  shadow_points: number;
  shadow_rewards: number;
  last_visit: string | null;
}

interface TopProduct {
  product_name: string;
  visits: number;
}

interface WalkInAnalyticsData {
  cohort: string;
  visitor_count: number;
  invite_priority: PriorityVisitor[];
  invite_priority_count: number;
  total_unconverted_spend: number;
  total_shadow_points: number;
  lost_credit_dollars: number;
  top_products: TopProduct[];
}

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v || 0);

const WalkInAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<WalkInAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<WalkInAnalyticsData>('/admin/bridge-club/walk-ins/analytics/');
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24 }}>Loading walk-in cohort analytics…</div>;
  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <button onClick={() => navigate(-1)} style={{ marginBottom: 12, background: 'transparent', border: 'none', color: '#2D5A27', cursor: 'pointer' }}>← Back</button>
        <div style={{ color: '#B91C1C' }}>{error || 'Not found'}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/admin/bridge-club')}
        style={{ marginBottom: 16, background: 'transparent', border: 'none', color: '#2D5A27', cursor: 'pointer' }}
      >
        ← Back to Bridge Club
      </button>

      <h1 style={{ marginBottom: 4 }}>Walk-In Cohort Analytics</h1>
      <div style={{ color: '#6B7280', marginBottom: 24 }}>
        {data.visitor_count} unmatched visitor{data.visitor_count === 1 ? '' : 's'} not yet claimed
      </div>

      {/* Headline cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <div style={{ background: '#F0FDF4', border: '1px solid #6EE7B7', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#065F46' }}>Invite-priority</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#065F46' }}>{data.invite_priority_count}</div>
          <div style={{ fontSize: 11, color: '#047857', marginTop: 2 }}>
            3+ visits · $200+ spend · or 500+ shadow points
          </div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Unclaimed spend</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{fmtCurrency(data.total_unconverted_spend)}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>across all walk-ins</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Shadow points</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{data.total_shadow_points.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>retroactive if all converted</div>
        </div>
        <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#92400E' }}>"Lost" loyalty</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#92400E' }}>${data.lost_credit_dollars}</div>
          <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
            credit already earned but unclaimed
          </div>
        </div>
      </div>

      {/* Invite-priority table */}
      <h3 style={{ marginBottom: 12 }}>Invite-priority walk-ins</h3>
      {data.invite_priority.length === 0 ? (
        <div style={{ color: '#6B7280', marginBottom: 24 }}>
          No walk-ins currently meet the invite-priority threshold.
        </div>
      ) : (
        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>Cardholder</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Visits</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Total spend</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Shadow pts</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Rewards owed</th>
                <th style={{ padding: 8 }}>Last visit</th>
              </tr>
            </thead>
            <tbody>
              {data.invite_priority.map((v) => (
                <tr
                  key={v.visitor_id}
                  onClick={() => navigate(`/admin/bridge-club/walk-ins/${v.visitor_id}`)}
                  style={{ borderTop: '1px solid #E5E7EB', cursor: 'pointer' }}
                >
                  <td style={{ padding: 8, color: '#2D5A27', fontWeight: 500 }}>{v.cardholder_name}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{v.visit_count}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{fmtCurrency(v.total_spend)}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{v.shadow_points.toLocaleString()}</td>
                  <td style={{ padding: 8, textAlign: 'right', color: v.shadow_rewards > 0 ? '#92400E' : '#6B7280', fontWeight: v.shadow_rewards > 0 ? 700 : 400 }}>
                    {v.shadow_rewards > 0 ? `$${v.shadow_rewards * 10}` : '—'}
                  </td>
                  <td style={{ padding: 8, color: '#6B7280' }}>{v.last_visit || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top products */}
      <h3 style={{ marginBottom: 12 }}>Top products bought by walk-ins</h3>
      {data.top_products.length === 0 ? (
        <div style={{ color: '#6B7280' }}>No POS line-item data yet.</div>
      ) : (
        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>#</th>
                <th style={{ padding: 8 }}>Product</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Cohort visits</th>
              </tr>
            </thead>
            <tbody>
              {data.top_products.map((p, idx) => (
                <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: 8, color: '#6B7280' }}>#{idx + 1}</td>
                  <td style={{ padding: 8, fontWeight: 500 }}>{p.product_name}</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{p.visits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WalkInAnalytics;
